<template>
	<tr :class="[rowClass, isSelected ? 'v-table-row--selected' : undefined]">
		<td
			v-for="(column, colIndex) in columns"
			:key="colIndex"
			:class="{
				...{
					'clickable-cell': column.key !== 'data-table-select',
					'select-cell': column.key === calculatedColumnNames.select,
					'cursor-pointer': shouldShowLink,
					[`text-${column.align}`]: column.align,
				},
				...column.cellProps?.class,
			}"
			:style="{
				width: 'auto',
				minWidth: 'auto',
				maxWidth: 'none',
				...computeColumnStyles(column, visibleColumnCount),
			}"
			@click="
				shouldShowLink &&
				column.type !== columnTypes.expand &&
				column.type !== columnTypes.selectRow
					? navigateToChild(item)
					: null
			"
		>
			<slot
				:name="`column.${column.key}`"
				:value="item[column.key]"
				:item="item[column.key]"
				:column="column"
				:row="item"
			>
				<TableCell
					:column="column"
					:row="item"
					:definition="definition"
					:is-selected="isSelected"
					:is-expanded="isExpanded"
					@expand="
						(args: boolean) => {
							handleExpand(item, args)
						}
					"
					@selected="
						(args: boolean) => {
							emit('update:selected', args)
						}
					"
				></TableCell>
			</slot>
		</td>
	</tr>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useDisplay } from 'vuetify'
import TableCell from './TableCell.vue'
import type {
	DataTableDefinition,
	DataTableHeader,
	DataTableRow,
} from '@/types/table.ts'
import { useTableStateStore } from '@/stores/system/tableState.ts'
import columnTypes from 'constants/columnTypes.ts'
import calculatedColumnNames from 'constants/calculatedColumnNames.ts'

const props = defineProps({
	item: {
		type: Object as () => DataTableRow,
		required: true,
	},
	columns: {
		type: Array as () => DataTableHeader[],
		required: true,
	},
	expanded: {
		type: Array,
		default: () => [],
	},
	isSelected: {
		type: Boolean,
		default: false,
	},
	isExpanded: {
		type: Boolean,
		default: false,
	},
	useSelect: {
		type: Boolean,
		default: false,
	},
	showExpand: {
		type: Boolean,
		default: false,
	},
	shouldShowLink: {
		type: Boolean,
		default: false,
	},
	rowClass: {
		type: String,
		default: '',
	},
	returnObject: {
		type: Boolean,
		default: false,
	},
	definition: {
		type: Object as () => DataTableDefinition,
		default: () => ({}),
	},
})

const emit = defineEmits([
	'update:selected',
	'toggle-expand',
	'navigateToChild',
	'update:expanded',
])

const tableState = useTableStateStore()
const { mdAndUp } = useDisplay()

// Aktueller Breakpoint-Name (xs|sm|md|lg|xl|xxl) fuer isColumnVisible().
const { name: displayName } = useDisplay()

const expandedItems = computed({
	get: () => props.expanded,
	set: (value) => {
		emit('update:expanded', value)
	},
})

const handleExpand = (item: any, expand: boolean) => {
	expandedItems.value = []
	if (expand) {
		expandedItems.value = [item]
	}
}

function navigateToChild(item: DataTableRow): void {
	if (props.definition.child && props.definition.child.setFilter) {
		const { tableName, name, filter, endpoint } = props.definition.child
		const childTableId = `${tableName || name}-${endpoint}`
		tableState.addOrUpdateFilter(childTableId, {
			definition: props.definition.child.filter!,
			value: item,
		})
	}
	emit('navigateToChild', item)
}

// Reihenfolge der Vuetify-Breakpoints, damit "-and-down"/"-and-up" aufgeloest
// werden koennen.
const BREAKPOINT_ORDER = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl']

/**
 * Ist die Spalte beim AKTUELLEN Breakpoint sichtbar? Wertet dieselben
 * `hide`-Angaben aus, die als Vuetify-Klasse an die Zelle gehen
 * (`hidden-xs`, `hidden-sm-and-down`, `hidden-md-and-up`, …).
 */
function isColumnVisible(column: DataTableHeader): boolean {
	if (column.hidden) return false
	if (!column.hide) return true

	const match = /^hidden-([a-z]+)(-and-(down|up))?$/.exec(column.hide)
	if (!match) return true

	const target = BREAKPOINT_ORDER.indexOf(match[1])
	const current = BREAKPOINT_ORDER.indexOf(displayName.value)
	if (target === -1 || current === -1) return true

	if (match[3] === 'down') return current > target
	if (match[3] === 'up') return current < target
	return current !== target
}

// Nur die tatsaechlich sichtbaren Spalten zaehlen. computeColumnStyles() schaltet
// oberhalb einer Schwelle von prozentualen auf feste Pixelbreiten um; zaehlte man
// dort die ausgeblendeten Spalten mit, kippte eine Tabelle allein durch eine
// Spalte, die beim aktuellen Breakpoint gar nicht gerendert wird, auf feste
// Breiten — deren Summe passte dann nicht mehr in den Container und die Tabelle
// scrollte horizontal (AAR-1812: Handwerkerauftrags-Liste bei 1200px).
const visibleColumnCount = computed(
	() => props.columns.filter((column) => isColumnVisible(column)).length
)

function computeColumnStyles(column: DataTableHeader, totalColumns: number) {
	const styles: Record<string, string> = {}

	// Determine if we should use percentages based on total number of columns
	const usePercentages = totalColumns <= 8 // For tables with 8 or fewer columns, percentages work well

	// Base width calculation from size property
	if (column.size) {
		// Choose sizing strategy based on column count
		if (usePercentages) {
			// Percentage-based for fewer columns
			switch (column.size) {
				case 'xs':
					styles.width = `${Math.max(5, 100 / (totalColumns * 2))}%`
					styles.minWidth = '60px'
					break
				case 'sm':
					styles.width = `${Math.max(8, 100 / (totalColumns * 1.5))}%`
					styles.minWidth = '80px'
					break
				case 'md':
					styles.width = `${Math.max(10, 100 / totalColumns)}%`
					styles.minWidth = '120px'
					break
				case 'lg':
					styles.width = `${Math.max(15, 100 / (totalColumns * 0.8))}%`
					styles.minWidth = '150px'
					break
				case 'xl':
					styles.width = `${Math.max(20, 100 / (totalColumns * 0.6))}%`
					styles.minWidth = '200px'
					break
			}
		} else {
			// Fixed widths for many columns
			switch (column.size) {
				case 'xs':
					styles.width = '80px'
					styles.minWidth = '60px'
					break
				case 'sm':
					styles.width = '120px'
					styles.minWidth = '80px'
					break
				case 'md':
					styles.width = '160px'
					styles.minWidth = '120px'
					break
				case 'lg':
					styles.width = '200px'
					styles.minWidth = '150px'
					break
				case 'xl':
					styles.width = '260px'
					styles.minWidth = '200px'
					break
			}
		}
	}

	// Explicit width property if defined
	if (column.width) {
		styles.width =
			typeof column.width === 'number' ? `${column.width}px` : column.width
	}

	if (column.minWidth) {
		styles.minWidth =
			typeof column.minWidth === 'number'
				? `${column.minWidth}px`
				: column.minWidth
	}

	// Feste Spaltenbreite ab `md`. Die Kombination ist nicht beliebig:
	// `table-layout: auto` behandelt eine gesetzte `width` als MINIMUM und
	// verteilt uebrigen Platz weiter, `max-width` an Zellen ignoriert es ganz.
	// Erst eine winzige Prozentbreite („ich brauche fast nichts") zusammen mit
	// `min-width` haelt die Spalte wirklich auf Mass; `max-width` bleibt als
	// Deckel fuer den Fall, dass die Tabelle je auf `fixed` umgestellt wird.
	if (column.fixedWidth && mdAndUp.value) {
		const fixed =
			typeof column.fixedWidth === 'number'
				? `${column.fixedWidth}px`
				: column.fixedWidth
		styles.width = '1%'
		styles.minWidth = fixed
		styles.maxWidth = fixed
	}

	// Special case handling for utility columns (always use fixed px values)
	if (column.key === calculatedColumnNames.select) {
		styles.width = '48px'
		styles.minWidth = '48px'
		styles.maxWidth = '48px'
		styles.flex = '0 0 48px'
	} else if (column.key === calculatedColumnNames.expand) {
		styles.width = 'auto'
		styles.minWidth = 'fit-content'
		styles.maxWidth = '100px'
		styles.flex = '0 1 auto'
	} else if (column.key === calculatedColumnNames.actions) {
		styles.width = '120px'
		styles.minWidth = '100px'
		styles.maxWidth = '140px'
		styles.flex = '0 0 120px'
	}

	// Set appropriate flex value based on width setting
	if (styles.width) {
		if (styles.width.includes('%')) {
			// Percentage widths should grow/shrink proportionally
			styles.flex = `1 1 ${styles.width}`
		} else {
			// Fixed widths don't stretch
			styles.flex = `0 0 ${styles.width}`
		}
	} else if (!column.size && !column.width) {
		// For columns with no sizing, make them flexible
		styles.flex = '1 1 auto'
	}

	// Handle hidden columns
	if (column.hidden) {
		styles.display = 'none'
	}

	// Content wrapping behavior - allow wrapping to multiple lines
	// for all columns except utility columns
	if (
		column.key !== calculatedColumnNames.select &&
		column.key !== calculatedColumnNames.expand
	) {
		// `word-break: break-word` ist ein Alt-Alias, den Browser auf
		// `overflow-wrap: break-word` abbilden — und das aendert die INTRINSISCHE
		// Mindestbreite einer Zelle NICHT. Ein langes Wort ("Gegensprechanlage")
		// blieb dadurch als Mindestbreite stehen, die Summe aller Spalten wurde
		// breiter als der Container und die Tabelle scrollte horizontal, obwohl
		// hier offensichtlich genau das verhindert werden sollte
		// (AAR-1812, HWA-Liste bei 1200px).
		//
		// `overflow-wrap: anywhere` zaehlt dagegen fuer die Mindestbreite mit —
		// aber nur die ELASTISCHEN Spalten bekommen es: Spalten mit `size`/`width`
		// haben eine bewusst gesetzte Mindestbreite und sollen ihren Inhalt (IDs,
		// Datumsangaben, Ortsnamen) NICHT mitten im Wort brechen. Die Spalten ohne
		// Groessenangabe fangen den Rest ab; sie brechen ohnehin zuerst an
		// Leerzeichen und nur im Notfall im Wort.
		styles.overflowWrap =
			column.size || column.width ? 'break-word' : 'anywhere'
		styles.whiteSpace = 'normal' // Allow text to wrap
		styles.overflow = 'visible'
	} else {
		// For utility columns, maintain single-line behavior
		styles.overflow = 'hidden'
		styles.textOverflow = 'ellipsis'
		styles.whiteSpace = 'nowrap'
	}
	// breakWords ist ein CSS-Wert ('normal' | 'break-all' | 'keep-all' | 'break-word'),
	// kein Schalter: eine Spalte mit eigener Angabe überschreibt den Default oben,
	// alle anderen behalten ihn. Die Bedingung war invertiert — dadurch wurde
	// wordBreak für jede Spalte OHNE Angabe auf undefined gesetzt (Default fiel weg)
	// und für jede Spalte MIT Angabe der konfigurierte Wert nie angewendet.
	if (column.breakWords) {
		styles.wordBreak = column.breakWords
	}

	return styles
}
</script>

<style scoped>
.expand-cell--cnt {
	width: 120px;
	min-width: 120px;
}

.expand-cell--btn {
	width: 48px;
	min-width: 48px;
}

.clickable-cell {
	width: 100%;
	height: 100%;
	padding: 12px;
}

/* AAR-962: kompaktere Auswahl-Spalte auf Mobile (xs) — Padding trimmen,
   Checkbox-Tap-Fläche bleibt unverändert */
@media (max-width: 599.98px) {
	/* computeColumnStyles() setzt die Select-Spalte inline auf 48px; per
	   !important auf Mobile auf 36px verschmälern + Padding trimmen. */
	td.select-cell {
		width: 36px !important;
		min-width: 36px !important;
		max-width: 36px !important;
		padding-inline: 2px !important;
	}
}

.cursor-pointer {
	cursor: pointer;
}

.expand-cell {
	width: 48px;
	min-width: 48px;
	max-width: 48px;
}

/* Custom row background classes */
tr.row-warning {
	background-color: rgba(var(--v-theme-warning), 0.15) !important;
}

tr.row-error {
	background-color: rgba(var(--v-theme-error), 0.15) !important;
}

tr.row-success {
	background-color: rgba(var(--v-theme-success), 0.15) !important;
}

tr.row-info {
	background-color: rgba(var(--v-theme-info), 0.15) !important;
}

/* Hover effect for colored rows */
tr.row-warning:hover:not(.v-data-table__tr--expanded),
tr.row-error:hover:not(.v-data-table__tr--expanded),
tr.row-success:hover:not(.v-data-table__tr--expanded),
tr.row-info:hover:not(.v-data-table__tr--expanded) {
	filter: brightness(0.95);
}

tr.v-data-table__selected {
	background-color: rgb(var(--v-theme-secondary)) !important;
	color: rgb(var(--v-theme-on-secondary)) !important;
}

tr.v-data-table__selected:hover {
	background-color: rgb(var(--v-theme-secondary)) !important;
	color: rgb(var(--v-theme-on-secondary)) !important;
}
</style>
